import { useEffect, useMemo, useState } from "react";
import { useBaasInstance } from "../Contexts/BaasInstanceContext";
import { fetchInternalTransferOptions } from "../Services/baasInternalTransfersApi";
import { formatBaasInstanceCredentialError } from "../Services/baasConsoleClient";

/**
 * Shared load of GET …/internal-transfer-options for Contas list UI.
 * Transfer enablement uses source `canTransferOut` (Root / PaySmart peers) —
 * never destination (Operacional / Overdraft / Program) balances.
 */
export const useBaasInternalTransferOptions = () => {
  const { selectedInstance, dataEpoch } = useBaasInstance();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sourceAccounts, setSourceAccounts] = useState([]);
  const [destinationAccounts, setDestinationAccounts] = useState([]);
  const [virtualAccounts, setVirtualAccounts] = useState([]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance) {
      setSourceAccounts([]);
      setDestinationAccounts([]);
      setVirtualAccounts([]);
      setError(null);
      setLoading(false);
      return undefined;
    }

    // Drop previous instance shells immediately so Contas cannot keep showing
    // VALUT-GLOBAL rows (or a prior 401 body) while the new card loads.
    setSourceAccounts([]);
    setDestinationAccounts([]);
    setVirtualAccounts([]);
    setError(null);
    setLoading(true);

    (async () => {
      try {
        const next = await fetchInternalTransferOptions(selectedInstance);
        if (cancelled) return;
        setSourceAccounts(next.sourceAccounts);
        setDestinationAccounts(next.destinationAccounts);
        setVirtualAccounts(next.virtualAccounts);
      } catch (err) {
        if (cancelled) return;
        setSourceAccounts([]);
        setDestinationAccounts([]);
        setVirtualAccounts([]);
        setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, dataEpoch]);

  const canTransferOut = useMemo(
    () => sourceAccounts.some((account) => account.canTransferOut === true),
    [sourceAccounts],
  );

  const friendlyError = useMemo(
    () => formatBaasInstanceCredentialError(error),
    [error],
  );

  return {
    loading,
    error: friendlyError,
    sourceAccounts,
    destinationAccounts,
    virtualAccounts,
    canTransferOut,
  };
};
